import { useEffect, useRef } from 'react';
import './ShortcutsOverlay.css';

interface ShortcutsOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

const isMac = typeof navigator !== 'undefined' && 
  (navigator.platform?.startsWith('Mac') || navigator.userAgent?.includes('Mac'));

const MOD = isMac ? '⌘' : 'Ctrl';

const SHORTCUTS = [
  { key: `${MOD} + Enter`, action: 'Run code' },
  { key: `${MOD} + S`, action: 'Share permalink' },
  { key: `${MOD} + E`, action: 'Open examples' },
  { key: '?', action: 'Toggle this overlay' },
  { key: 'Esc', action: 'Close overlay' },
];

export default function ShortcutsOverlay({ isOpen, onClose }: ShortcutsOverlayProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const modal = modalRef.current;
    const focusable = modal.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener('keydown', handleTab);
    first?.focus();
    return () => document.removeEventListener('keydown', handleTab);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <div className="shortcuts-backdrop" onClick={onClose} />
      <div ref={modalRef} className="shortcuts-modal" role="dialog" aria-label="Keyboard shortcuts">
        <div className="shortcuts-header">
          <span className="shortcuts-title">── SHORTCUTS ──</span>
          <button className="shortcuts-close" onClick={onClose} aria-label="Close">[close]</button>
        </div>
        <div className="shortcuts-content">
          <table className="shortcuts-table">
            <tbody>
              {SHORTCUTS.map((s) => (
                <tr key={s.key}>
                  <td className="shortcuts-key">{s.key}</td>
                  <td className="shortcuts-action">{s.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
